import { Toast as BaseToast } from '@base-ui/react/toast';
import styled, { keyframes } from 'styled-components';

import { Button } from '@components/button';
import { IconCheckboxChecked } from '@components/icons/checkbox-checked';
import { IconFormIndicatorError } from '@components/icons/form-indicator-error';
import { IconFormIndicatorInfo } from '@components/icons/form-indicator-info';
import { IconOpen } from '@components/icons/open';
import { IconOpenNewTab } from '@components/icons/open-new-tab';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import { hasToastActions, remove, ToastData, ToastVariant } from './use-toaster';

type BaseToastObject = BaseToast.Root.ToastObject<ToastData>;

type ToastProps = { toast: BaseToastObject };

const variantIconMap: Record<ToastVariant, JSX.Element> = {
  info: <IconFormIndicatorInfo color="elementIndigo100" />,
  error: <IconFormIndicatorError color="elementRed100" />,
  success: <IconCheckboxChecked color="elementGreen100" />,
};

const OpenUrlButtons = ({ url }: { url: string }) => {
  const { t } = useScopedTranslate(translations);

  return (
    <Stack space={2} orientation="horizontal">
      <Button
        href={url}
        variant="secondary"
        size="sm"
        font="semibold12"
        iconLeft={<IconOpen />}
        text={t('open')}
      />
      <Button
        href={url}
        target="_blank"
        variant="secondary"
        size="sm"
        font="semibold12"
        iconRight={<IconOpenNewTab />}
        text={t('openInNewTab')}
      />
    </Stack>
  );
};

const CustomButtons = ({ data }: { data: ToastData }) => {
  if (!data.customButtons || data.customButtons.length === 0) {
    return null;
  }

  return (
    <Stack space={2} orientation="horizontal">
      {data.customButtons.map((button, index) => (
        <Button
          key={index}
          variant="secondary"
          size="sm"
          font="semibold12"
          iconLeft={button.icon}
          text={button.text}
          onClick={button.onClick}
        />
      ))}
    </Stack>
  );
};

export const Toast = ({ toast }: ToastProps) => {
  const data = toast.data;
  if (!data) {
    return null;
  }

  const icon = variantIconMap[data.variant];
  const hasTimer = data.durationSeconds > 0;
  // Action toasts show the visible progress bar; plain message toasts run the same
  // animation invisibly, purely as the auto-close timer (paused on hover all the same).
  const showProgressBar = hasTimer && hasToastActions(data);

  return (
    <ToastRoot
      toast={toast}
      swipeDirection={['down']}
      data-cy="toast"
      onClick={() => remove(toast.id)}
    >
      <ToastContent>
        <Stack space={4}>
          <Stack
            space={2}
            orientation="horizontal"
            align={toast.description ? 'flex-start' : 'center'}
          >
            {icon}
            <TextColumn>
              <ToastTitle data-cy="toast-title">
                <Text color="textPrimary" font="regular14">
                  {toast.title}
                </Text>
              </ToastTitle>
              {toast.description && (
                <ToastDescription>
                  <Text color="textSecondary" font="regular14">
                    {toast.description}
                  </Text>
                </ToastDescription>
              )}
            </TextColumn>
          </Stack>
          <CustomButtons data={data} />
          {data.url && <OpenUrlButtons url={data.url} />}
          {showProgressBar && (
            <ToastProgressBar
              variant={data.variant}
              durationSeconds={data.durationSeconds}
              onDone={() => remove(toast.id)}
            />
          )}
        </Stack>
        {hasTimer && !showProgressBar && (
          <HiddenTimer
            data-cy="toast-timer"
            aria-hidden="true"
            style={{ animationDuration: `${data.durationSeconds}s` }}
            onAnimationEnd={() => remove(toast.id)}
          />
        )}
      </ToastContent>
    </ToastRoot>
  );
};

const TextColumn = styled.div({
  flex: '1 0 0',
  minWidth: 0,
});

const ToastRoot = styled(BaseToast.Root)({
  '--gap': pxToRem(12),
  '--peek': pxToRem(12),
  '--scale': 'calc(max(0, 1 - (var(--toast-index) * 0.1)))',
  '--shrink': 'calc(1 - var(--scale))',
  '--height': 'var(--toast-frontmost-height, var(--toast-height))',
  '--offset-y':
    'calc(var(--toast-offset-y) * -1 + (var(--toast-index) * var(--gap) * -1) + var(--toast-swipe-movement-y))',

  position: 'absolute',
  right: 0,
  bottom: 0,
  left: 'auto',
  margin: 0,
  boxSizing: 'border-box',
  width: '100%',
  height: 'var(--height)',

  backgroundColor: theme.color.backgroundLightDark,
  border: `1px solid ${theme.color.border10}`,
  borderRadius: pxToRem(8),
  padding: theme.space[6],
  boxShadow: theme.shadow.fancy,
  backgroundClip: 'padding-box',
  cursor: 'default',
  userSelect: 'none',
  WebkitUserSelect: 'none',

  transformOrigin: 'bottom center',
  transition: 'transform 0.5s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.5s, height 0.15s',
  zIndex: 'calc(1000 - var(--toast-index))',
  transform:
    'translateX(var(--toast-swipe-movement-x)) ' +
    'translateY(calc(var(--toast-swipe-movement-y) - (var(--toast-index) * var(--peek)) - (var(--shrink) * var(--height)))) ' +
    'scale(var(--scale))',

  '&[data-expanded]': {
    transform: 'translateX(var(--toast-swipe-movement-x)) translateY(var(--offset-y))',
    height: 'var(--toast-height)',
  },

  '&[data-starting-style]': {
    transform: 'translateY(150%)',
  },

  // Fade and slide down together when dismissed by the timer (per Figma "Fade out @ 100%").
  // Only the frontmost toast (index 0) slides — toasts behind it just fade.
  // `min(1, var(--toast-index))` is 0 for the frontmost and 1 otherwise, zeroing
  // the translate for any non-frontmost toast.
  // Swipe dismissals override the transform below.
  '&[data-ending-style]': {
    opacity: 0,
    transform: 'translateY(calc(50% * (1 - min(1, var(--toast-index)))))',
    transition: 'opacity 0.4s ease-out, transform 0.4s cubic-bezier(0.22, 1, 0.36, 1)',
  },

  '&[data-limited]': {
    opacity: 0,
  },

  '&[data-ending-style][data-swipe-direction="up"]': {
    transform: 'translateY(calc(var(--toast-swipe-movement-y) - 150%))',
  },
  '&[data-ending-style][data-swipe-direction="down"]': {
    transform: 'translateY(calc(var(--toast-swipe-movement-y) + 150%))',
  },
  '&[data-ending-style][data-swipe-direction="left"]': {
    transform: 'translateX(calc(var(--toast-swipe-movement-x) - 150%)) translateY(var(--offset-y))',
  },
  '&[data-ending-style][data-swipe-direction="right"]': {
    transform: 'translateX(calc(var(--toast-swipe-movement-x) + 150%)) translateY(var(--offset-y))',
  },

  // Bridge the visual gap between stacked toasts so hovering the stack stays expanded.
  '&::after': {
    content: '""',
    position: 'absolute',
    top: '100%',
    width: '100%',
    left: 0,
    height: 'calc(var(--gap) + 1px)',
  },
});

const ToastContent = styled(BaseToast.Content)({
  position: 'relative',
  overflow: 'hidden',
  transition: 'opacity 0.25s',

  '&[data-behind]': {
    opacity: 0,
  },

  '&[data-expanded]': {
    opacity: 1,
  },
});

const ToastTitle = styled(BaseToast.Title)({
  margin: 0,
  padding: 0,
  font: 'inherit',
});

const ToastDescription = styled(BaseToast.Description)({
  margin: 0,
  padding: 0,
  font: 'inherit',
});

type ToastProgressBarProps = {
  variant: ToastVariant;
  durationSeconds: number;
  onDone: () => void;
};

const ToastProgressBar = ({ variant, durationSeconds, onDone }: ToastProgressBarProps) => {
  return (
    <ProgressBarContainer aria-label={`Progress ${variant} for ${durationSeconds} seconds`}>
      <ProgressBarFill
        data-variant={variant}
        style={{ animationDuration: `${durationSeconds}s` }}
        onAnimationEnd={onDone}
      />
    </ProgressBarContainer>
  );
};

const slideRight = keyframes`
  from { width: 0%; }
  to { width: 100%; }
`;

// Invisible auto-close timer for toasts without a visible progress bar. It runs the
// same animation purely for its duration + animationend, and pauses on hover via
// [data-toaster-paused] exactly like the visible bar. ToastContent is overflow:hidden,
// so the 0-height element never shows.
const HiddenTimer = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 0;
  height: 0;
  animation: ${slideRight} linear forwards;

  [data-toaster-paused] & {
    animation-play-state: paused;
  }
`;

const ProgressBarContainer = styled.div({
  width: '100%',
  height: pxToRem(6),
  borderRadius: pxToRem(6),
  backgroundColor: theme.color.uiTrans10,
  overflow: 'hidden',
});

const ProgressBarFill = styled.div`
  height: ${pxToRem(6)};
  border-radius: ${pxToRem(6)};
  width: 0%;
  animation: ${slideRight} linear forwards;

  &[data-variant='success'] {
    background: ${theme.color.green100};
  }
  &[data-variant='error'] {
    background: ${theme.color.red100};
  }
  &[data-variant='info'] {
    background: ${theme.color.indigo100};
  }

  [data-toaster-paused] & {
    animation-play-state: paused;
  }
`;

const translations = {
  en: {
    open: 'Open',
    openInNewTab: 'Open in new tab',
  },
};
